"use client";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useRouter, useSearchParams } from "next/navigation";
import { safeLogEvent } from "@/libs/shared/analytics/safeLogEvent";
import { useAnalyticsContext } from "@/libs/shared/analytics/AnalyticsProvider";
import { setMessageHandler } from "@/libs/shared/messages/messages.slice";
import { useLoginMutation } from "@/libs/auth/redux/auth.api";
import { LoginPayloadModel } from "@/libs/auth/redux/auth.types";

export function useLogin() {
  const router = useRouter();
  const { t } = useTranslation();
  const analytics = useAnalyticsContext();
  const [login, { data: user, isLoading, error, isSuccess }] =
    useLoginMutation();
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const redirect = searchParams?.get("redirect")!;

  useEffect(() => {
    if (
      error &&
      !(error as { data: { deletion_pending: boolean } })?.data
        ?.deletion_pending
    ) {
      const errorData = (error as { data: { is_pt?: boolean } })?.data;
      const isNotPt = errorData?.is_pt === false;

      dispatch(
        setMessageHandler({
          title: isNotPt ? t("Login:error_not_pt") : t("Login:error"),
          subtitle: isNotPt ? t("Login:error_not_pt_subtitle") : undefined,
          type: "error",
        }),
      );
    } else if (isSuccess) {
      safeLogEvent(analytics, "login-success");
      if (window?.profitwell) {
        window?.profitwell("user_id", user?.id);
      }

      if (redirect) {
        router.push(decodeURIComponent(redirect));
      } else {
        router.replace("/dashboard");
      }
    }
  }, [analytics, dispatch, error, isSuccess, router, t, redirect]);

  const loginHandler = (data: LoginPayloadModel) => {
    void login({
      ...data,
      rememberToken: false,
    });
  };

  return {
    loginHandler,
    isLoading,
    deletionPending: (error as { data: { deletion_pending: boolean } })?.data
      ?.deletion_pending,
  };
}
